import type { CarbonIconType } from "@carbon/icons-react";
import { ChevronRight } from "@carbon/icons-react";
import { Breadcrumbs, Link, Skeleton } from "@heroui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type Params, useMatches } from "react-router";
import { Tooltip } from "src/general/components/common/Tooltip/Tooltip";
import {
  type EntityCrumb,
  getRouteCrumb,
  isEntityCrumb,
  type StaticCrumb,
} from "src/utils/breadcrumbUtils";

/**
 * Longest parent label left on its own; past this it may clip, so it gets a
 * tooltip. Deliberately below the 34ch width cap: the exact clip point depends
 * on the glyphs, and a tooltip repeating a label that happened to fit costs
 * nothing, while a missing one on a clipped label is the bug this guards
 * against.
 */
const MAX_LABEL_LENGTH = 30;

/** Slightly wider than the threshold, so a label that fits is never clipped. */
const LABEL_WIDTH = `${MAX_LABEL_LENGTH + 4}ch`;

type Breadcrumb = {
  label: string;
  href?: string;
  /** Section roots carry the icon the main menu uses. */
  icon?: CarbonIconType;
  /** Render a placeholder while the label (usually an entity name) is loading */
  isLoading?: boolean;
};

/**
 * The open entity is named in full: it is the label the page is about, and it
 * is the one crumb a keyboard user cannot reach, so hiding it behind hover
 * would put it out of reach entirely. It wraps rather than clipping, since the
 * row does not scroll.
 */
const CurrentCrumb = ({ icon: Icon, label }: Breadcrumb) => (
  <span
    aria-current="page"
    className="breadcrumbs__link hover:no-underline"
    data-current="true"
  >
    {!!Icon && (
      <Icon aria-hidden className="shrink-0 mr-2 inline align-text-bottom" />
    )}
    {label}
  </span>
);

/**
 * A parent crumb clips, with the full name in a tooltip. The link itself is the
 * tooltip trigger: HeroUI's default trigger is a focusable `div role="button"`,
 * which inside an anchor is invalid markup and a second tab stop for the same
 * name. With one focusable element the tooltip also opens on keyboard focus,
 * not just on hover.
 */
const ParentCrumb = ({ href, icon: Icon, label }: Breadcrumb) => {
  const link = (
    <Link className="breadcrumbs__link" href={href}>
      {!!Icon && <Icon aria-hidden className="shrink-0 mr-2" />}
      {/* Clipped so that a long name cannot stretch the row. */}
      <span
        className="block overflow-hidden text-ellipsis whitespace-nowrap"
        style={{ maxWidth: LABEL_WIDTH }}
      >
        {label}
      </span>
    </Link>
  );

  if (label.length <= MAX_LABEL_LENGTH) {
    return link;
  }

  return (
    <Tooltip
      asChild
      content={label}
      placement="bottom"
      classNames={{ content: "wrap-anywhere [word-break:normal]" }}
    >
      {link}
    </Tooltip>
  );
};

const BreadcrumbItem = ({
  breadcrumb,
  isCurrent,
}: {
  breadcrumb: Breadcrumb;
  isCurrent: boolean;
}) => (
  <Breadcrumbs.Item className={isCurrent ? "min-w-0 shrink" : undefined}>
    {() => (
      <>
        {breadcrumb.isLoading ? (
          <Skeleton className="inline-block h-4 w-24 align-middle" />
        ) : isCurrent ? (
          <CurrentCrumb {...breadcrumb} />
        ) : (
          <ParentCrumb {...breadcrumb} />
        )}
        {!isCurrent && (
          <ChevronRight
            aria-hidden
            className="breadcrumbs__separator"
            data-slot="breadcrumbs-separator"
          />
        )}
      </>
    )}
  </Breadcrumbs.Item>
);

/** A crumb whose text is fixed by the route itself. */
const StaticRouteCrumb = ({
  crumb,
  href,
  isCurrent,
}: {
  crumb: StaticCrumb;
  href: string;
  isCurrent: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <BreadcrumbItem
      breadcrumb={{ href, icon: crumb.icon, label: crumb.label(t) }}
      isCurrent={isCurrent}
    />
  );
};

/** A crumb naming an entity, read through the hook the route names. */
const EntityRouteCrumb = ({
  crumb,
  href,
  isCurrent,
  params,
}: {
  crumb: EntityCrumb;
  href: string;
  isCurrent: boolean;
  params: Params;
}) => {
  const { t } = useTranslation();
  const { isLoading, label, notFound } = crumb.useLabel(params);

  /* An entity that failed to load leaves nothing to name the crumb with, so
     it says why instead. */
  if (!isLoading && !label) {
    return (
      <BreadcrumbItem
        breadcrumb={{
          href,
          label: t(notFound ? "error::Not found" : "error::Error"),
        }}
        isCurrent={isCurrent}
      />
    );
  }

  return (
    <BreadcrumbItem
      breadcrumb={{ href, isLoading, label }}
      isCurrent={isCurrent}
    />
  );
};

const BreadcrumbsRow = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-4">
    {/* Link colors are themed in index.css (.breadcrumbs__link). */}
    <Breadcrumbs>{children}</Breadcrumbs>
    <hr />
  </div>
);

export const PageBreadcrumbs = () => {
  const matches = useMatches();

  const routeCrumbs = matches.flatMap((match) => {
    const crumb = getRouteCrumb(match.handle);
    return crumb ? [{ crumb, match }] : [];
  });

  /* A lone crumb is not a trail: a list page names itself in its own header and
     in the main menu, so the row would only repeat that. */
  if (routeCrumbs.length < 2) {
    return null;
  }

  return (
    <BreadcrumbsRow>
      {routeCrumbs.map(({ crumb, match }, index) => {
        const isCurrent = index === routeCrumbs.length - 1;

        return isEntityCrumb(crumb) ? (
          <EntityRouteCrumb
            key={match.id}
            crumb={crumb}
            href={match.pathname}
            isCurrent={isCurrent}
            params={match.params}
          />
        ) : (
          <StaticRouteCrumb
            key={match.id}
            crumb={crumb}
            href={match.pathname}
            isCurrent={isCurrent}
          />
        );
      })}
    </BreadcrumbsRow>
  );
};
